import React, { useState, useEffect, useRef, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import getDataTableStyles from '../../utils/tableStyles';
import { Alert, Button, Spinner, Modal, Form, InputGroup, Card, Row, Col, Badge, Table } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import axiosInstance from '../../api/axios';
import PhotoAlbum from 'react-photo-album';
import { useTranslation } from 'react-i18next';

function Catalogue() {
  const { t } = useTranslation();
  const { isDark } = useContext(ThemeContext) || {};
  const customStyles = getDataTableStyles(isDark);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Forms
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const initialForm = {
    sku: '',
    nombre: '',
    descripcion: '',
    familia: '',
    marca: '',
    precio_base_usd: '',
    unidades_por_caja: 1,
    stock_real: '',
    stock_minimo: 0,
    stock_reservado: 0,
    stock_visible: true,
    categoria_id: '',
    unidad_medida: 'UND',
    imagen_base64: '',
    imagen_url_existente: ''
  };
  const [formData, setFormData] = useState(initialForm);
  const [importFile, setImportFile] = useState(null);
  const [showImportErrorModal, setShowImportErrorModal] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const [editingId, setEditingId] = useState(null);

  const handleCategoryChange = (catId) => {
    const selectedCat = categories.find(c => c.id === catId);
    const prefix = selectedCat?.prefijo_sku ? `${selectedCat.prefijo_sku}-` : 'A-';
    
    // 1. Immediate state update for instant UI feedback (0ms)
    setFormData(prev => ({
      ...prev,
      categoria_id: catId,
      sku: (!editingId && catId) ? prefix : prev.sku
    }));

    // 2. Fetch exact next SKU from Backend asynchronously in background
    if (!editingId && catId) {
      axiosInstance.get(`/admin/catalog/next-sku?categoria_id=${catId}`)
        .then(response => {
          if (response.data?.success && response.data?.next_sku) {
            setFormData(prev => ({ ...prev, sku: response.data.next_sku }));
          }
        })
        .catch(() => {});
    }
  };

  // Gallery state
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [gallerySearch, setGallerySearch] = useState('');
  const galleryFileInputRef = useRef(null);

  // Filters
  const [filterActive, setFilterActive] = useState(''); // '' = Todos, 'true' = Activos, 'false' = Inactivos
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/admin/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error cargando categorias', err);
    }
  };

  const fetchFamilies = async () => {
    try {
      const response = await axiosInstance.get('/admin/catalog/families');
      if (response.data.success) {
        setFamilies(response.data.data || []);
      }
    } catch (err) {
      console.error('Error cargando familias', err);
    }
  };

  const fetchProducts = async (page = currentPage, currentPerPage = perPage) => {
    try {
      setLoading(true);
      let params = [];
      params.push(`page=${page}`);
      params.push(`per_page=${currentPerPage}`);
      if (filterActive !== '') params.push(`is_active=${filterActive}`);
      if (filterCategory !== '') params.push(`categoria_id=${filterCategory}`);
      if (filterFamily !== '') params.push(`familia=${encodeURIComponent(filterFamily)}`);
      if (filterSearch !== '') params.push(`search=${encodeURIComponent(filterSearch)}`);
      const query = params.length > 0 ? `?${params.join('&')}` : '';

      const response = await axiosInstance.get(`/admin/catalog${query}`);
      if (response.data.success) {
        setProducts(response.data.data);
        if (response.data.meta) {
          setTotalRows(response.data.meta.total !== undefined ? response.data.meta.total : response.data.data.length);
        }
      }
    } catch (err) {
      setError('Ocurrió un error al cargar el inventario.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGallery = async () => {
    try {
      setLoadingGallery(true);
      const response = await axiosInstance.get('/admin/catalog/gallery');
      if (response.data.success) {
        setGalleryImages(response.data.data);
      }
    } catch (err) {
      console.error('Error cargando galería', err);
    } finally {
      setLoadingGallery(false);
    }
  };

  const openGallery = () => {
    setGallerySearch('');
    setShowGalleryModal(true);
    fetchGallery();
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingGallery(true);
    const uploadData = new FormData();
    for (let i = 0; i < files.length; i++) {
      uploadData.append('images[]', files[i]);
    }

    try {
      const response = await axiosInstance.post('/admin/catalog/gallery/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        fetchGallery();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error subiendo imagen a la galería.');
    } finally {
      setUploadingGallery(false);
      if (galleryFileInputRef.current) galleryFileInputRef.current.value = '';
    }
  };

  const selectGalleryImage = (imgSrc) => {
    setFormData(prev => ({ ...prev, imagen_url_existente: imgSrc, imagen_base64: '' }));
    setShowGalleryModal(false);
  };

  useEffect(() => {
    fetchCategories();
    fetchFamilies();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    if (!filterSearch) {
      fetchProducts(1, perPage);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      fetchProducts(1, perPage);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [filterActive, filterCategory, filterFamily, filterSearch]);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const payload = { ...formData, is_active: true };
      
      let response;
      if (editingId) {
        response = await axiosInstance.put(`/admin/catalog/${editingId}`, payload);
      } else {
        response = await axiosInstance.post('/admin/catalog', payload);
      }

      if (response.data.success) {
        setShowCreateModal(false);
        setEditingId(null);
        setFormData(initialForm);
        fetchProducts(); // refresh list
        fetchFamilies(); // refresh families if new one added
      }
    } catch (err) {
      console.error(err);
      alert('Error guardando producto. Revisa los datos.');
    } finally {
      setProcessing(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(initialForm);
    setShowCreateModal(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.id);
    setFormData({
      sku: row.sku || '',
      nombre: row.nombre || '',
      descripcion: row.descripcion || '',
      familia: row.familia || '',
      marca: row.marca || '',
      precio_base_usd: row.precio_base_usd || (row.precios && row.precios.length > 0 ? row.precios[0].amount : ''),
      precio_b_usd: row.precio_b_usd || (row.precios && row.precios.length > 1 ? row.precios[1].amount : ''),
      unidades_por_caja: row.unidades_por_caja || 1,
      stock_real: row.stock_real || 0,
      stock_minimo: row.stock_minimo || 0,
      stock_reservado: row.stock_reservado || 0,
      stock_visible: row.stock_visible !== undefined ? row.stock_visible : true,
      categoria_id: row.categoria_id || '',
      unidad_medida: row.unidad_medida || 'UND',
      imagen_base64: '',
      imagen_url_existente: row.imagen_url || ''
    });
    setShowCreateModal(true);
  };

  // Download official excel template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/admin/catalog/template', {
        responseType: 'blob'
      });
      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_carga_masiva_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar plantilla:', err);
      alert('No se pudo descargar la plantilla oficial.');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const ext = file.name.split('.').pop().toLowerCase();
      if (['xlsx', 'xls', 'csv'].includes(ext)) {
        setImportFile(file);
      } else {
        alert('Formato no soportado. Por favor sube un archivo Excel (.xlsx, .xls) o CSV.');
      }
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return alert('Por favor selecciona un archivo Excel (.xlsx) o CSV primero.');
    
    setProcessing(true);
    const data = new FormData();
    data.append('file', importFile);

    try {
      const response = await axiosInstance.post('/admin/catalog/import', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        alert(response.data.message || 'Productos importados con éxito.');
        setShowImportModal(false);
        setImportFile(null);
        fetchProducts(1, perPage);
      }
    } catch (err) {
      console.error('Error al importar productos:', err);
      const errorData = err.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        setImportErrors(errorData.errors);
        setShowImportModal(false);
        setShowImportErrorModal(true);
      } else {
        alert('Error al importar productos: ' + (errorData?.message || err.message));
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (id) => {
    if(!window.confirm("¿Seguro que deseas dar de baja este producto? Ya no aparecerá disponible para los vendedores.")) return;
    try {
      await axiosInstance.delete(`/admin/catalog/${id}`);
      fetchProducts();
    } catch (err) {
      alert('Error al desactivar el producto.');
    }
  };

  const columns = [
    {
      name: t('th_img', 'Img'),
      selector: row => row.imagen_url,
      width: '60px',
      cell: row => {
        if (!row.imagen_url) {
          return <div style={{width: '32px', height: '32px', background: '#eee', borderRadius: '4px'}} className="d-flex align-items-center justify-content-center"><i className="mdi mdi-image text-muted"></i></div>;
        }
        return (
          <img 
            src={row.imagen_url} 
            alt="prod" 
            style={{width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px'}} 
          />
        );
      }
    },
    {
      name: 'SKU',
      selector: row => row.sku,
      sortable: true,
      minWidth: '120px'
    },
    {
      name: t('th_name', 'Nombre'),
      selector: row => row.nombre,
      sortable: true,
      minWidth: '200px'
    },
    {
      name: t('th_price', 'Precio ($)'),
      selector: row => row.precio_base_usd || (row.precios && row.precios.length > 0 ? row.precios[0].amount : 0),
      sortable: true,
      minWidth: '130px',
      cell: row => {
        const p = row.precio_base_usd || (row.precios && row.precios.length > 0 ? row.precios[0].amount : 0);
        return <span className="fw-bold text-primary">${parseFloat(p || 0).toFixed(2)}</span>;
      }
    },
    {
      name: t('units_per_box_badge', 'Und/Caja'),
      selector: row => row.unidades_por_caja || 1,
      sortable: true,
      width: '100px',
      cell: row => <span className="badge bg-secondary">{row.unidades_por_caja || 1} {t('units_per_box_badge', 'u/caja')}</span>
    },
    {
      name: t('th_category', 'Categoría'),
      selector: row => row.category ? row.category.nombre : t('no_category', 'Sin Categoría'),
      sortable: true,
      minWidth: '150px'
    },
    {
      name: t('th_family_brand', 'Familia / Marca'),
      selector: row => row.familia || row.marca || '',
      sortable: true,
      minWidth: '180px',
      cell: row => (
        <div className="py-1">
          {row.familia && (
            <div className="mb-1">
              <span className="badge" style={{ backgroundColor: '#183eeb', color: '#fff', fontSize: '0.75rem', fontWeight: '600' }}>
                <i className="mdi mdi-factory me-1"></i>{row.familia}
              </span>
            </div>
          )}
          {row.marca && row.marca !== row.familia && (
            <div>
              <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
                <i className="mdi mdi-tag-outline me-1 text-muted"></i>{row.marca}
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      name: t('real_stock', 'Stock Real'),
      selector: row => row.stock_real,
      sortable: true,
      minWidth: '150px',
      cell: row => {
        const stock = row.stock_real ?? 0;
        const minStock = row.stock_minimo ?? 0;
        if (stock === 0) {
          return (
            <div className="py-1">
              <span className="badge bg-danger p-2">
                <i className="mdi mdi-alert-circle me-1"></i>{t('stock_out_of_stock', 'Agotado (0)')}
              </span>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                <i className="mdi mdi-eye-off me-1"></i>{t('stock_hidden_in_app', 'Oculto en App')}
              </small>
            </div>
          );
        }
        if (stock <= minStock) {
          return (
            <span className="badge bg-warning text-dark p-2">
              <i className="mdi mdi-alert me-1"></i>{t('stock_low', 'Bajo')} ({stock} {row.unidad_medida || 'UND'})
            </span>
          );
        }
        return (
          <span className="badge bg-success p-2">
            <i className="mdi mdi-check-circle me-1"></i>{stock} {row.unidad_medida || 'UND'}
          </span>
        );
      }
    },
    {
      name: t('reserved_stock', 'Reservado'),
      selector: row => row.stock_reservado,
      sortable: true,
      width: '100px',
      cell: row => <span className="text-warning">{row.stock_reservado}</span>
    },
    {
      name: t('th_status', 'Estado'),
      selector: row => row.is_active,
      width: '120px',
      cell: row => (
        row.is_active 
          ? <label className="badge bg-success">{t('status_active', 'Activo')}</label>
          : <label className="badge bg-danger">{t('status_inactive', 'Inactivo')}</label>
      )
    },
    {
      name: t('th_actions', 'Acciones'),
      width: '120px',
      minWidth: '100px',
      cell: row => (
        <div className="d-flex align-items-center gap-1">
          <button 
            className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" 
            style={{ width: '32px', height: '32px', padding: 0 }} 
            onClick={() => openEditModal(row)}
            title={t('btn_edit_product', 'Editar Producto')}
          >
            <i className="mdi mdi-pencil" style={{ fontSize: '1.05rem' }}></i>
          </button>
          {row.is_active ? (
            <button 
              className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleToggleStatus(row.id)}
              title={t('btn_deactivate', 'Dar de Baja')}
            >
              <i className="mdi mdi-delete-outline" style={{ fontSize: '1.1rem' }}></i>
            </button>
          ) : (
            <button 
              className="btn btn-sm btn-outline-success rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleToggleStatus(row.id)}
              title={t('btn_activate_product', 'Reactivar Producto')}
            >
              <i className="mdi mdi-check-circle-outline" style={{ fontSize: '1.1rem' }}></i>
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className='page-header'>
        <h3 className='page-title'>{t('inventory_management', 'Gestión de Inventario')}</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="!#" onClick={e => e.preventDefault()}>{t('menu_sales_catalog', 'Catálogo')}</a></li>
            <li className="breadcrumb-item active" aria-current="page">{t('menu_product_catalog', 'Inventario')}</li>
          </ol>
        </nav>
      </div>

      <div className='row'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-body'>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className='card-title mb-0'>{t('filters_and_search', 'Filtros y Búsqueda')}</h4>
              </div>
              <div className="row mb-2">
                <div className="col-md-3 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('search_product_label', 'Buscar Producto')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder={t('search_product_placeholder', 'Buscar por Nombre, SKU, Marca o Familia...')} 
                      value={filterSearch} 
                      onChange={e => setFilterSearch(e.target.value)} 
                      className="customer-filter-control w-100 shadow-none"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('th_status', 'Estatus')}</Form.Label>
                    <Form.Select 
                      value={filterActive} 
                      onChange={e => setFilterActive(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">{t('status_all', 'Todos los Estados')}</option>
                      <option value="true">{t('status_active_in_catalog', 'Activos (En catálogo)')}</option>
                      <option value="false">{t('status_inactive_disabled', 'Inactivos (Baja)')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-4 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('th_category', 'Categoría / Departamento')}</Form.Label>
                    <Form.Select 
                      value={filterCategory} 
                      onChange={e => setFilterCategory(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">{t('all_categories', 'Todas las Categorías')}</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.parent_id ? `\u00A0\u00A0↳ ${c.nombre} (${c.prefijo_sku || ''})` : `📁 ${c.nombre.toUpperCase()}`}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('th_family_manufacturer', 'Familia (Fabricante)')}</Form.Label>
                    <Form.Select 
                      value={filterFamily} 
                      onChange={e => setFilterFamily(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">{t('all_families', 'Todas las Familias')}</option>
                      {families.map((fam, idx) => (
                        <option key={idx} value={fam}>
                          🏭 {fam}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='row mt-3'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-body'>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <h4 className='card-title mb-0'>{t('products_and_stock', 'Productos y Stock')}</h4>
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => setShowImportModal(true)}
                    style={{ borderRadius: '10px' }}
                  >
                    <i className="mdi mdi-file-excel me-1"></i>{t('btn_import_excel', 'Importar Masiva')}
                  </Button>
                  <Button 
                    style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }} 
                    onClick={openCreateModal}
                  >
                    <i className="mdi mdi-plus me-1"></i>{t('btn_add_product', 'Nuevo Producto')}
                  </Button>
                </div>
              </div>
              
              {error && <Alert variant='danger'>{error}</Alert>}
              
              <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                <div style={{ width: '100%' }}>
                  <DataTable 
                    columns={columns} 
                    data={products} 
                    pagination 
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    onChangePage={page => {
                      setCurrentPage(page);
                      fetchProducts(page, perPage);
                    }}
                    onChangeRowsPerPage={(newPerPage, page) => {
                      setPerPage(newPerPage);
                      setCurrentPage(page);
                      fetchProducts(page, newPerPage);
                    }}
                    paginationComponentOptions={{
                      rowsPerPageText: t('rows_per_page', 'Filas por página:'),
                      rangeSeparatorText: t('of_range', 'de'),
                      selectAllRowsItem: false,
                    }}
                    customStyles={customStyles} 
                    progressPending={loading}
                    progressComponent={<LoadingState title={t('loading_catalogue', 'Cargando catálogo de productos...')} description={t('loading_catalogue_sub', 'Consultando inventario, precios base y categorías')} />}
                    noDataComponent={<div className="my-5 text-muted">{t('no_products_in_inventory', 'No hay productos en el inventario.')}</div>}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* CREATE MODAL */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Form onSubmit={handleCreateOrUpdate}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
            <Modal.Title>{editingId ? t('modal_edit_product', 'Editar Producto') : t('modal_create_product', 'Crear Nuevo Producto')}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff' }}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('step_select_category', '1. Seleccionar Categoría')}</Form.Label>
                  <Form.Select value={formData.categoria_id} onChange={e => handleCategoryChange(e.target.value)} style={{ border: '2px solid #183eeb', borderRadius: '10px', backgroundColor: '#f4f6ff' }}>
                    <option value="">{t('choose_category_placeholder', 'Seleccione Categoría')}</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.parent_id ? `\u00A0\u00A0↳ ${cat.nombre} (${cat.prefijo_sku || ''})` : `📁 ${cat.nombre.toUpperCase()}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_unit_measure', 'Unidad de Medida')}</Form.Label>
                  <Form.Select required value={formData.unidad_medida} onChange={e => setFormData({...formData, unidad_medida: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }}>
                    <option value="UND">UND (Unidad)</option>
                    <option value="KG">KG (Kilogramo)</option>
                    <option value="PQTE">PQTE (Paquete)</option>
                    <option value="BANDEJA">BANDEJA</option>
                    <option value="RISTRA">RISTRA</option>
                    <option value="DISPLAY">DISPLAY</option>
                    <option value="TRIPACK">TRIPACK</option>
                    <option value="BOLSA">BOLSA</option>
                    <option value="PAQUETE">PAQUETE</option>
                    <option value="CAJA">CAJA</option>
                    <option value="LITRO">LITRO</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_sku', 'SKU (Código)')}</Form.Label>
                  <Form.Control type="text" required value={formData.sku} onChange={e => setFormData({...formData, sku: e.target.value})} disabled={!!editingId} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} placeholder="ej: Q-0001 / A-0001" />
                </Form.Group>
              </div>
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_product_name', 'Nombre del Producto')}</Form.Label>
                  <Form.Control type="text" required value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_family_manufacturer', 'Familia (Fabricante / Casa Comercial)')}</Form.Label>
                  <Form.Control 
                    type="text" 
                    list="modal-families-datalist"
                    value={formData.familia} 
                    onChange={e => setFormData({...formData, familia: e.target.value})} 
                    style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                    placeholder="ej: Todo Import C.A. / Incaragua / L Prados" 
                  />
                  <datalist id="modal-families-datalist">
                    {families.map((fam, idx) => (
                      <option key={idx} value={fam} />
                    ))}
                  </datalist>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_brand', 'Marca Comercial (Línea Específica)')}</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.marca} 
                    onChange={e => setFormData({...formData, marca: e.target.value})} 
                    style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                    placeholder="ej: Americandy / Big Bom / Proembuti" 
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_price_usd', 'Precio USD ($)')}</Form.Label>
                  <Form.Control type="number" step="0.01" min="0" required value={formData.precio_base_usd} onChange={e => setFormData({...formData, precio_base_usd: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_units_per_box', 'Unidades por Caja / Bulto')}</Form.Label>
                  <Form.Control type="number" min="1" required value={formData.unidades_por_caja} onChange={e => setFormData({...formData, unidades_por_caja: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_physical_stock', 'Stock Físico')}</Form.Label>
                  <Form.Control type="number" min="0" required value={formData.stock_real} onChange={e => setFormData({...formData, stock_real: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_min_stock', 'Stock Mínimo')}</Form.Label>
                  <Form.Control type="number" min="0" required value={formData.stock_minimo} onChange={e => setFormData({...formData, stock_minimo: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_reserved_stock', 'Reservado')}</Form.Label>
                  <Form.Control type="number" min="0" required value={formData.stock_reservado} onChange={e => setFormData({...formData, stock_reservado: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_product_image', 'Imagen del Producto')}</Form.Label>
              <div className="d-flex align-items-center mb-2">
                <Button className="text-nowrap" style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }} onClick={openGallery}>
                  <i className="mdi mdi-image-multiple me-1"></i> {formData.imagen_url_existente ? t('btn_change_gallery', 'Cambiar Imagen de Galería') : t('btn_select_gallery', 'Seleccionar de Galería')}
                </Button>
                {formData.imagen_url_existente && (
                  <Button variant="outline-danger" className="ms-2" style={{ borderRadius: '10px' }} onClick={() => setFormData({...formData, imagen_url_existente: ''})}>
                    <i className="mdi mdi-delete"></i> {t('btn_remove_image', 'Quitar')}
                  </Button>
                )}
              </div>
              {formData.imagen_url_existente && (
                <div className="mt-2">
                  <img 
                    src={formData.imagen_url_existente} 
                    alt="Preview" 
                    height="60" 
                    style={{borderRadius: '10px', objectFit: 'cover', border: '2px solid #00024f'}} 
                  />
                </div>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_description', 'Descripción')}</Form.Label>
              <Form.Control as="textarea" rows={3} value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #00024f' }}>
            <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)} style={{ color: '#010028', borderColor: '#010028', borderRadius: '10px' }}>{t('btn_cancel', 'Cancelar')}</Button>
            <Button type="submit" disabled={processing} style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" /> : (editingId ? t('btn_save_changes', 'Guardar Cambios') : t('btn_save_product', 'Guardar Producto'))}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* IMPORT MODAL */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" centered>
        <Form onSubmit={handleImport}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
            <div className="d-flex align-items-center">
              <i className="mdi mdi-file-excel me-2 fs-4 text-success"></i>
              <div>
                <Modal.Title className="h5 mb-0 fw-bold">{t('modal_import_products_title', 'Importación Masiva de Productos')}</Modal.Title>
                <small className="text-white-50" style={{ fontSize: '0.8rem' }}>{t('modal_import_products_subtitle', 'Carga de inventario y catálogo oficial ALPINCA')}</small>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff', padding: '24px' }}>
            {/* Step 1: Download official template */}
            <div className="p-3 mb-4 rounded border bg-light d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center">
                  <i className="mdi mdi-file-download-outline text-primary me-2 fs-5"></i>
                  {t('download_template_title', '1. Descargar Plantilla Oficial Excel')}
                </h6>
                <p className="text-muted small mb-0">
                  {t('download_template_desc', 'Descarga el archivo base (.xlsx) con los encabezados y ejemplos preconfigurados.')}
                </p>
              </div>
              <Button 
                variant="outline-primary" 
                onClick={handleDownloadTemplate}
                className="d-flex align-items-center shadow-sm"
                style={{ borderRadius: '10px', fontWeight: 'bold' }}
              >
                <i className="mdi mdi-microsoft-excel me-2 text-success fs-5"></i>
                {t('btn_download_template', 'Descargar Plantilla (.xlsx)')}
              </Button>
            </div>

            {/* Step 2: Info alert */}
            <div className="alert alert-info py-2 px-3 mb-4" style={{ borderRadius: '10px', fontSize: '0.85rem' }}>
              <div className="fw-bold mb-1">
                <i className="mdi mdi-information me-1 text-primary"></i>
                {t('import_columns_required', 'Columnas obligatorias en el archivo:')}
              </div>
              <code style={{ fontSize: '0.8rem', color: '#00024f' }}>
                Departamento | Subcategoría | Código SKU | Descripción / Nombre | Familia (Fabricante) | Marca | Unidad de Medida | Cantidad por Caja | Existencia | Precio Unitario ($)
              </code>
            </div>
            
            {/* Step 3: Drag & Drop upload area */}
            <div 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`text-center p-4 border-dashed rounded ${isDragging ? 'bg-light border-primary' : ''}`} 
              style={{ border: isDragging ? '2px dashed #183eeb' : '2px dashed #cbd5e1', borderRadius: '15px', backgroundColor: '#f8fafc', transition: 'all 0.2s ease' }}
            >
              <i className="mdi mdi-cloud-upload-outline d-block mb-2" style={{ fontSize: '42px', color: '#183eeb' }}></i>
              <div className="fw-bold mb-1" style={{ color: '#010028', fontSize: '1.05rem' }}>
                {t('select_inventory_file', 'Arrastra tu archivo Excel o CSV aquí')}
              </div>
              <p className="text-muted small mb-3">{t('supported_formats', 'Formatos soportados: .xlsx, .xls, .csv (Máx 20MB)')}</p>
              
              <Form.Control 
                type="file" 
                accept=".xlsx,.xls,.csv" 
                required={!importFile} 
                onChange={e => setImportFile(e.target.files[0])} 
                className="d-none"
                id="excel-catalog-upload"
              />
              <div>
                <Button as="label" htmlFor="excel-catalog-upload" style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px', cursor: 'pointer' }}>
                  <i className="mdi mdi-folder-open-outline me-1"></i> {t('btn_browse_files', 'Examinar Archivos')}
                </Button>
              </div>

              {importFile && (
                <div className="mt-3 text-success fw-bold p-2 bg-white rounded border d-inline-flex align-items-center shadow-sm">
                  <i className="mdi mdi-file-excel text-success me-2" style={{ fontSize: '1.3rem' }}></i>
                  <span>{importFile.name}</span>
                  <span className="text-muted small ms-2">({(importFile.size / 1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #00024f' }}>
            <Button variant="outline-secondary" onClick={() => { setShowImportModal(false); setImportFile(null); }} style={{ color: '#010028', borderColor: '#010028', borderRadius: '10px' }}>
              {t('btn_cancel', 'Cancelar')}
            </Button>
            <Button type="submit" disabled={processing || !importFile} style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="mdi mdi-upload me-1"></i>}
              {t('btn_upload_import', 'Subir e Importar')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* BULK IMPORT ERROR REPORT MODAL */}
      <Modal show={showImportErrorModal} onHide={() => setShowImportErrorModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#dc3545', color: '#ffffff', borderBottom: '3px solid #b02a37' }}>
          <Modal.Title className="d-flex align-items-center">
            <i className="mdi mdi-alert-rhombus me-2" style={{ fontSize: '1.5rem' }}></i>
            {t('modal_error_report_title', 'Reporte de Errores en Carga Masiva')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Alert variant="danger" className="d-flex align-items-center mb-4" style={{ borderRadius: '10px' }}>
            <i className="mdi mdi-alert-circle me-3" style={{ fontSize: '2rem' }}></i>
            <div>
              <strong className="d-block mb-1">{t('import_rejected_title', 'La importación fue rechazada por completo (Rollback Transaccional)')}</strong>
              <span>{t('import_rejected_sub_prod', 'Se detectaron errores de validación o duplicados en las filas detalladas a continuación. Ningún producto fue guardado en el catálogo.')}</span>
            </div>
          </Alert>

          <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="align-middle mb-0">
              <thead className="sticky-top bg-light">
                <tr>
                  <th className="text-center" style={{ width: '80px' }}>{t('th_row', 'Fila #')}</th>
                  <th style={{ width: '200px' }}>{t('th_field', 'Campo')}</th>
                  <th>{t('th_reason', 'Motivo del Error')}</th>
                </tr>
              </thead>
              <tbody>
                {importErrors.map((err, idx) => (
                  <tr key={idx}>
                    <td className="text-center fw-bold text-danger">
                      <Badge bg="danger" style={{ fontSize: '0.85rem' }}>{err.row || err.fila || '-'}</Badge>
                    </td>
                    <td className="fw-bold text-dark">{err.field || err.campo || 'General'}</td>
                    <td className="text-danger font-weight-bold">{err.reason || err.motivo || err.message}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fc', borderTop: '1px solid #dee2e6' }}>
          <Button 
            variant="secondary" 
            onClick={() => { setShowImportErrorModal(false); setShowImportModal(true); }}
            style={{ borderRadius: '10px' }}
          >
            <i className="mdi mdi-refresh me-1"></i> {t('btn_fix_and_retry', 'Corregir Archivo y Reintentar')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Gallery Modal */}
      <Modal show={showGalleryModal} onHide={() => setShowGalleryModal(false)} size="xl" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
          <div className="d-flex align-items-center">
            <i className="mdi mdi-image-multiple me-2 fs-4 text-primary"></i>
            <div>
              <Modal.Title className="h5 mb-0 fw-bold">{t('modal_gallery_select_title', 'Seleccionar Imagen de la Galería')}</Modal.Title>
              <small className="text-white-50" style={{ fontSize: '0.8rem' }}>{t('gallery_select_subtitle', 'Haz clic sobre la imagen que deseas asignar a este producto')}</small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f8fafc', padding: '20px' }}>
          {/* Top Action & Search Bar */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div style={{ maxWidth: '350px', width: '100%' }}>
              <div className="position-relative">
                <i 
                  className="mdi mdi-magnify position-absolute text-muted" 
                  style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.1rem', zIndex: 5, pointerEvents: 'none' }}
                />
                <Form.Control
                  type="text"
                  placeholder={t('search_file_placeholder', 'Buscar por nombre de archivo o SKU...')}
                  value={gallerySearch}
                  onChange={e => setGallerySearch(e.target.value)}
                  className="w-100 shadow-none"
                  style={{
                    paddingLeft: '36px',
                    paddingRight: '12px',
                    height: '38px',
                    borderRadius: '8px',
                    border: '1.5px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    fontSize: '0.85rem'
                  }}
                />
              </div>
            </div>

            <div>
              <input
                type="file"
                multiple
                accept="image/png, image/jpeg, image/webp"
                style={{ display: 'none' }}
                ref={galleryFileInputRef}
                onChange={handleGalleryUpload}
              />
              <Button
                size="sm"
                style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px', fontWeight: 'bold' }}
                onClick={() => galleryFileInputRef.current && galleryFileInputRef.current.click()}
                disabled={uploadingGallery || loadingGallery}
              >
                {uploadingGallery ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="mdi mdi-cloud-upload me-1"></i>}
                {uploadingGallery ? t('uploading_label', 'Subiendo...') : t('btn_upload_new_image', 'Subir Nueva Imagen')}
              </Button>
            </div>
          </div>

          {/* Gallery Content Area */}
          <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
            {loadingGallery ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                <h6 className="mt-3 fw-bold" style={{ color: '#00024f' }}>{t('loading_media_gallery', 'Cargando galería multimedia...')}</h6>
                <p className="text-muted small">{t('querying_server_images', 'Consultando imágenes del servidor central Rutex')}</p>
              </div>
            ) : (() => {
              const filtered = galleryImages.filter(img => {
                if (!gallerySearch) return true;
                const name = (img.src || '').toLowerCase();
                return name.includes(gallerySearch.toLowerCase());
              });

              if (filtered.length === 0) {
                return (
                  <div className="text-center py-5">
                    <i className="mdi mdi-image-off" style={{ fontSize: '48px', color: '#94a3b8' }}></i>
                    <h6 className="mt-2 fw-bold text-dark">{t('no_images_found_gallery', 'No se encontraron imágenes')}</h6>
                    <p className="text-muted small mb-3">
                      {gallerySearch ? t('no_images_found_gallery', 'No hay imágenes que coincidan con la búsqueda.') : t('no_images_in_gallery_system', 'La galería del servidor está vacía.')}
                    </p>
                    <Button 
                      size="sm" 
                      variant="outline-primary" 
                      style={{ borderRadius: '8px' }}
                      onClick={() => galleryFileInputRef.current && galleryFileInputRef.current.click()}
                    >
                      <i className="mdi mdi-upload me-1"></i> {t('btn_upload_now', 'Subir imágenes ahora')}
                    </Button>
                  </div>
                );
              }

              return (
                <Row className="g-3">
                  {filtered.map((img, idx) => {
                    const isSelected = formData.imagen_url_existente === img.src;
                    const fileName = img.src.split('/').pop();

                    return (
                      <Col xs={6} sm={4} md={3} lg={3} key={idx}>
                        <Card 
                          className={`h-100 shadow-sm ${isSelected ? 'border-primary' : 'border-0'}`}
                          style={{ 
                            cursor: 'pointer', 
                            transition: 'all 0.2s ease', 
                            borderRadius: '12px',
                            overflow: 'hidden',
                            backgroundColor: '#ffffff',
                            border: isSelected ? '2px solid #183eeb' : '1px solid #e2e8f0'
                          }}
                          onClick={() => selectGalleryImage(img.src)}
                          onMouseEnter={e => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.borderColor = '#183eeb';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(24, 62, 235, 0.18)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.borderColor = isSelected ? '#183eeb' : '#e2e8f0';
                            e.currentTarget.style.boxShadow = '';
                          }}
                        >
                          <div style={{ height: '130px', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', position: 'relative', borderBottom: '1px solid #f1f5f9' }}>
                            <img 
                              src={img.src} 
                              alt={fileName} 
                              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                            />
                            {isSelected && (
                              <Badge bg="primary" className="position-absolute top-0 end-0 m-2 shadow-sm" style={{ borderRadius: '6px' }}>
                                <i className="mdi mdi-check-bold me-1"></i> {t('assigned_label', 'Asignada')}
                              </Badge>
                            )}
                          </div>
                          <Card.Footer className="p-2 text-center bg-white border-0">
                            <small className="text-truncate d-block fw-semibold text-dark" title={fileName} style={{ fontSize: '0.78rem' }}>
                              {fileName}
                            </small>
                          </Card.Footer>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              );
            })()}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #e2e8f0' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowGalleryModal(false)} style={{ borderRadius: '8px' }}>
            {t('btn_close', 'Cerrar')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default Catalogue;